import { doc, getDoc } from 'firebase/firestore';
import { Badge, BellRing, Heart, LockOpen } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Banner } from '../../shared/components/Banner';
import { ButtonTiny } from '../../shared/components/ButtonTiny';
import { LoadingScreen } from '../../shared/components/LoadingScreen';
import { OllieFloating } from '../../shared/components/OllieFloating';
import { SpeechBubble } from '../../shared/components/SpeechBubble';
import { firestore } from '../../shared/config/firebase';
import { useAnalytics } from '../../shared/hooks/useAnalytics';
import { useLoggedInUser } from '../../shared/hooks/useLoggedInUser';
import type { CrossPlatformPackage } from '../../shared/hooks/useRevenueCat';
import { useRevenueCat } from '../../shared/hooks/useRevenueCat';
import { ROUTES } from '../../shared/routing/routes';
import PaywallFeature from './components/PaywallFeature';
import { PaywallTimelineStep } from './components/PaywallTimelineStep';
import PaywallPaymentDrawer from './PaywallPaymentDrawer';

const PaywallPage: React.FC = () => {
    const navigate = useNavigate();
    const {
        currentOffering,
        isLoading,
        error,
        setError,
        purchasePackage,
        refreshCustomerInfo,
        restorePurchases,
    } = useRevenueCat();

    const user = useLoggedInUser();

    const [selectedPackage, setSelectedPackage] = useState<CrossPlatformPackage | null>(null);
    const [isPurchasing, setIsPurchasing] = useState(false);
    const [paywallShowPerWeekMode, setPaywallShowPerWeekMode] = useState<boolean | null>(null);
    const { logEvent } = useAnalytics();

    // Auto-select the first package when offerings load
    useEffect(() => {
        if (currentOffering) {
            setSelectedPackage(currentOffering.monthly);
        }
    }, [currentOffering]);

    // Fetch paywall_show_per_week_mode flag from Firestore
    useEffect(() => {
        const fetchPaywallFlag = async () => {
            try {
                const appSettingsRef = doc(firestore, 'metadata', 'app_settings');
                const appSettingsSnap = await getDoc(appSettingsRef);

                if (appSettingsSnap.exists()) {
                    const data = appSettingsSnap.data();
                    const flagValue = data.paywall_show_per_week_mode;
                    setPaywallShowPerWeekMode(Boolean(flagValue));
                } else {
                    console.warn('App settings document not found, using default value');
                    setPaywallShowPerWeekMode(false);
                }
            } catch (error) {
                console.error('Error fetching paywall flag:', error);
                setPaywallShowPerWeekMode(false);
            }
        };

        fetchPaywallFlag();
    }, []);

    const handlePurchase = async () => {
        if (!selectedPackage) return;

        setIsPurchasing(true);
        try {
            await purchasePackage(selectedPackage);
            // Refresh customer info to get latest subscription status
            await refreshCustomerInfo();
            // If successful, user will be redirected by the useEffect above
        } catch (err) {
            // Error is handled in the hook
        } finally {
            setIsPurchasing(false);
        }
    };

    const handlePaywallDismiss = () => {
        logEvent("paywall_dismissed");
        localStorage.setItem(`paywall_seen_${user.uid}`, 'true');
        navigate(ROUTES.onboarding.goToAppStore);
    };

    const handleRestorePurchases = useCallback(async () => {
        await restorePurchases();
    }, [restorePurchases]);

    const features = [
        "Your personalised OCD plan",
        "Unlimited Ollie support",
        "Every SOS tool & audio exercise",
        "All OCD lessons",
        "Future upgrades included"
    ];

    if (user.isProUser) {
        return <Navigate to={ROUTES.onboarding.goToAppStore} replace />;
    }

    if (isLoading || !selectedPackage || paywallShowPerWeekMode === null) {
        return <LoadingScreen />;
    }

    const pageVariants = {
        annually: {
            titlePill: "Your first week is on us.",
            titlePillColor: "bg-osPink-2",
            featuresPillColor: "bg-osPink-2",
            featuresPillTextColor: "text-osBlack",
            subtitle: `Enjoy one week free, then ${selectedPackage.pricePerMonthString}/month`,
            subtitleFinePrint: `(billed at ${selectedPackage.pricePerYearString}/year)`,
            timelineSteps: [
                {
                    title: "Today - Start free trial",
                    description: "Unlock unlimited Ollie chats, support, lessons and tools.",
                    icon: <LockOpen size={16} strokeWidth={3} />
                },
                {
                    title: "Day 4 - Trial Reminder",
                    description: "We'll send you a reminder through email that your trial is ending soon.",
                    icon: <BellRing size={16} strokeWidth={3} />
                },
                {
                    title: "Day 7 - You'll be charged",
                    description: "Your subscription starts. Continue your plan automatically, cancel anytime.",
                    icon: <Heart size={16} strokeWidth={3} />
                }
            ]
        },
        monthly: {
            titlePill: "Your first week is on us.",
            titlePillColor: "bg-osPink-2",
            featuresPillColor: "bg-osPink-2",
            featuresPillTextColor: "text-osBlack",
            subtitle: `Enjoy one week free, then ${selectedPackage.pricePerMonthString}/month`,
            subtitleFinePrint: `(billed at ${selectedPackage.pricePerMonthString}/month)`,
            timelineSteps: [
                {
                    title: "Today - Start free trial",
                    description: "Unlock unlimited Ollie chats, support, lessons and tools.",
                    icon: <LockOpen size={16} strokeWidth={3} />
                },
                {
                    title: "Day 4 - Trial Reminder",
                    description: "We'll send you a reminder through email that your trial is ending soon.",
                    icon: <BellRing size={16} strokeWidth={3} />
                },
                {
                    title: "Day 7 - You'll be charged",
                    description: "Your subscription starts. Continue your plan automatically, cancel anytime.",
                    icon: <Heart size={16} strokeWidth={3} />
                }
            ]
        },
        weekly: {
            titlePill: "Today.",
            titlePillColor: "bg-osGrey-3",
            featuresPillColor: "bg-osBlack",
            featuresPillTextColor: "text-white",
            subtitle: `Start your plan today for ${selectedPackage.pricePerWeekString}/week`,
            subtitleFinePrint: `(billed at ${selectedPackage.pricePerWeekString}/week)`,
            timelineSteps: [
                {
                    title: "Today - Billed",
                    description: `Your subscription starts, billed at ${selectedPackage.pricePerWeekString}/week.`,
                    icon: <LockOpen size={16} strokeWidth={3} />
                },
                {
                    title: "Work towards OCD relief",
                    description: "Unlock unlimited Ollie chats, support, lessons and tools",
                    icon: <Badge size={16} strokeWidth={3} />
                },
            ]
        }
    }

    const selectedPageVariant = pageVariants[selectedPackage.billingPeriod as keyof typeof pageVariants];

    return (
        <div className='flex flex-col items-center justify-center h-svh max-w-md mx-auto'>
            <title>Paywall</title>
            <div className='flex-1 flex flex-col items-center gap-6 px-8 pb-8 safe-pt-2 overflow-y-auto'>
                <nav className="flex-shrink-0 flex items-center justify-between w-full max-w-main gap-3">
                    <button className="text-osGrey-2" onClick={handleRestorePurchases}>restore purchase</button>
                    <div className='flex-shrink-0'>
                        <ButtonTiny variant="close" label="not now" onClick={handlePaywallDismiss} secondsToActivate={4} />
                    </div>
                </nav>
                <div className='flex flex-col items-center justify-center'>
                    <h1 className='text-osBlack text-[28px] font-semibold leading-tight'>Start your personalized OCD plan today</h1>
                    <p className={`text-osBlack font-semibold rounded-[12px] ${selectedPageVariant.titlePillColor} px-[10px] py-[2px]`}>{selectedPageVariant.titlePill}</p>
                </div>
                <div className='flex flex-col items-center justify-center'>
                    <SpeechBubble text="Let's start working towards Relief Together" size='small' className='!max-w-[70%] -mb-9 !border-osBlack !py-3 !px-0' />
                    <OllieFloating />
                </div>
                <div className='flex flex-col items-center justify-center'>
                    <p className='text-osBlack font-semibold'>{selectedPageVariant.subtitle}</p>
                    <p className='text-osBlack text-sm'>{selectedPageVariant.subtitleFinePrint}</p>
                </div>
                <div className='flex flex-col items-start justify-start w-full'>
                    {selectedPageVariant.timelineSteps.map((step, index) => (
                        <PaywallTimelineStep
                            key={index}
                            title={step.title}
                            description={step.description}
                            isFirstStep={index === 0}
                            isLastStep={index === selectedPageVariant.timelineSteps.length - 1}
                            icon={step.icon}
                        />
                    ))}
                </div>
                <Banner text="What you unlock today" className='text-sm' />
                <div className="flex flex-col items-end gap-2 w-full">
                    <div className={`font-semibold rounded-[12px] ${selectedPageVariant.featuresPillColor} px-[10px] py-2 ${selectedPageVariant.featuresPillTextColor}`}>pro</div>
                    {features.map((feature, index) => (
                        <PaywallFeature
                            key={index}
                            text={feature}
                        />
                    ))}
                </div>
                <p className='text-center text-osGrey-2 text-sm'>
                    We never want cost to stand in the way of care. If paying isn't possible for you right now, email finaid@obsessless.com, we set aside financial aid to make sure those who truly need help can still get it.
                </p>
                <p className="text-center text-osGrey-2 mt-12">
                    Signed in as {user.email}
                </p>
            </div>
            <PaywallPaymentDrawer
                currentOffering={currentOffering}
                paywallShowPerMonthMode={paywallShowPerWeekMode}
                selectedPackage={selectedPackage}
                referralCode={user.referralCode}
                onPackageSelect={setSelectedPackage}
                onSubscribe={handlePurchase}
                isLoading={isLoading}
                error={error}
                isPurchasing={isPurchasing}
                onErrorDismiss={() => setError({
                    customerInfo: null,
                    offerings: null,
                    purchase: null,
                    restore: null,
                })}
            />
        </div>
    );
};

export default PaywallPage; 